import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, TemplateRef } from '@angular/core';
import { CfPopoverBase } from 'components/popover-host/popover-interface';
import { PopoverController } from 'providers/popover-controller';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CsSelectPopoverShownSection, filterSelectPopoverSections, toSelectPopoverSections } from './cs-select-popover-sections';

export interface CsSelectPopoverSubmenu {
  title?: TranslatedString;  // title for mobile submenu header
  options: CsSelectPopoverOption[];
}

export interface CsSelectPopoverOption {
  name: TranslatedString;
  value: string;
  description?: TranslatedString;
  selectState?: boolean;  // for multiple selection mode
  handler?: (event?: Event) => void | Promise<void>;  // optional click handler (event from capture-cover for lockClick)
  icon?: string;  // icon name (leading icon, shown on left)
  contentTemplate?: TemplateRef<any>;  // custom content template to display after the label
  helpIcon?: boolean;  // show help icon on the right
  controlType?: 'checkbox' | 'toggle' | 'radio';  // control type: checkbox (default), toggle, or radio
  submenu?: CsSelectPopoverSubmenu;  // nested submenu options
  disabled?: boolean;  // disabled option (cannot be selected)
}

export interface CsSelectPopoverSubsection {
  header: TranslatedString;  // section header text (e.g., "BODY COMPOSITION")
  options: CsSelectPopoverOption[];  // options in this section
}

@Component({
  selector: 'cs-select-popover',
  templateUrl: 'cs-select-popover.html',
  host: { 'class': 'select-popover' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsSelectPopover extends CfPopoverBase implements OnInit {
  title: TranslatedString;
  options: CsSelectPopoverOption[];
  subsections: CsSelectPopoverSubsection[];  // alternative to flat options array
  value: string;
  bottomSheet: boolean = false;
  // Presented as a centred dialog on a large screen rather than a dropdown under the control that
  // opened it (#5549). It reads as a dialog, so it carries its title the way the bottom sheet does.
  centered: boolean = false;
  targetWidth?: number;  // Width of the target input element
  selectMultiple: boolean = false;  // Enable multiple selection with checkboxes
  backButton: boolean = false;  // Show back button in header (for nested menus on mobile)
  searchable: boolean = false;  // Show search input to filter options
  // one extra action in the title bar, beside the title; running it closes the menu (#5549)
  headerAction?: { text: TranslatedString; handler: () => void };

  // search state
  searchQuery: string = '';
  // the sections the menu draws: both inputs as one list of sections, cut down by the search query.
  // A flat `options` list is one section with no header, so every row kind and the search work the
  // same whichever input the menu was given.
  shownSections: CsSelectPopoverShownSection[] = [];

  // submenu state (for mobile navigation within bottom sheet)
  activeSubmenu: CsSelectPopoverSubmenu | null = null;
  activeSubmenuParentTitle: TranslatedString = untranslatedString('');  // original title to restore on back

  constructor(
    private popoverCtrl: PopoverController,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit() {
    // Set the width CSS variable on desktop if targetWidth is provided
    if (this.targetWidth && !hasClientSmallScreen() && !this.bottomSheet) {
      const element = this.elementRef.nativeElement as HTMLElement;
      element.style.setProperty('--popover-width', `${this.targetWidth}px`);
    }
    this.shownSections = filterSelectPopoverSections(this.allSections(), this.searchQuery);
  }

  onSearchInput(query: string) {
    this.searchQuery = query;
    this.shownSections = filterSelectPopoverSections(this.allSections(), query);
    this.cdr.markForCheck();
  }

  onWillDismiss(data: any): void {
    if (this.selectMultiple) {
      // return array of selected values in multiple selection mode
      const allOptions = this.getAllOptions();
      data.value = allOptions
        .filter(option => option.selectState)
        .map(option => option.value);
    } else {
      // return single value in single selection mode
      data.value = this.value;
    }
  }

  // every section, whichever input the menu was given, before any search
  private allSections() {
    return toSelectPopoverSections(this.options, this.subsections);
  }

  // every option in every section, whatever the search is showing
  private getAllOptions(): CsSelectPopoverOption[] {
    return this.allSections().flatMap(section => section.options);
  }

  close() {
    void this.popoverCtrl.dismiss(this, {});
  }

  /** set once the title bar's action has run, so a second tap during the closing animation is ignored */
  private headerActionRan = false;

  /** the title bar's extra action (e.g. Reset). It changes the state the rows were drawn from,
   *  so the menu closes rather than stay open showing what was true a moment ago. Runs at most
   *  once: nothing stops taps while the menu animates away, and a double tap would otherwise
   *  run the action twice. */
  handleHeaderActionClick() {
    if (this.headerActionRan) {
      return;
    }
    this.headerActionRan = true;
    this.headerAction?.handler();
    this.close();
  }

  handleBackClick() {
    // if in submenu, go back to main menu
    if (this.activeSubmenu) {
      this.navigateBackFromSubmenu();
    } else {
      this.close();
    }
  }

  selectOption(option: CsSelectPopoverOption, event?: Event) {
    if (!this.selectMultiple) {
      // single selection mode - select and close
      // don't set value for submenu parents (they aren't final selections)
      if (!option.submenu) {
        this.value = option.value;
      }

      if (option.submenu) {
        // has submenu - navigate to submenu on mobile only
        // on desktop, submenu is shown via hover, clicking does nothing
        if (this.bottomSheet) {
          this.navigateToSubmenu(option);
        }
        // on desktop, do nothing - submenu is already shown via hover
      } else if (option.handler) {
        // dismiss first so history.back() pops the popover's overlay entry,
        // then call handler (which may push a new page via navCtrl)
        void (async () => {
          await this.popoverCtrl.dismiss(this, {});
          void option.handler(event);
        })();
      } else {
        // no handler, just close
        this.close();
      }
    } else {
      // multiple selection mode - toggle selection state
      option.selectState = !option.selectState;

      this.runMultiSelectHandler(option, event);
    }
  }

  // run a row's handler while the menu stays open. If the handler is async, redraw once it
  // settles: it may change other rows' states after an await, long after the tap's own redraw,
  // and this OnPush menu is not redrawn otherwise
  private runMultiSelectHandler(option: CsSelectPopoverOption, event?: Event) {
    const result = option.handler?.(event);
    if (result instanceof Promise) {
      void result.finally(() => this.cdr.markForCheck());
    }
  }

  // show submenu (different behavior for mobile vs desktop)
  private navigateToSubmenu(option: CsSelectPopoverOption) {
    if (!option.submenu) return;

    if (this.bottomSheet) {
      // mobile: navigate within bottom sheet, change title
      this.activeSubmenuParentTitle = this.title;
      // a submenu without its own title falls back to the parent option's name; both are
      // TranslatedString since #4368, so nothing needs wrapping here
      this.title = option.submenu.title || option.name;
    }
    // desktop: just show submenu panel (no title change)

    this.activeSubmenu = option.submenu;
    this.cdr.markForCheck();
  }

  // hide submenu (desktop only - mouse leave)
  private hideSubmenu() {
    this.activeSubmenu = null;
    this.cdr.markForCheck();
  }

  // navigate back from submenu to main menu (mobile)
  navigateBackFromSubmenu() {
    if (this.bottomSheet) {
      this.title = this.activeSubmenuParentTitle;
      this.activeSubmenuParentTitle = untranslatedString('');
    }
    this.activeSubmenu = null;
    this.cdr.markForCheck();
  }

  showIcons() {
    const allOptions = this.getAllOptions();
    for (let option of allOptions) {
      if (option.icon !== undefined) {
        return true;
      }
    }
    return false;
  }

  onContainerClick(event: Event) {
    // Only dismiss if in bottom sheet mode and clicking on the container itself
    // (not on child elements like radio buttons)
    if (this.bottomSheet && event.target === event.currentTarget) {
      this.close();
    }
  }

  // check if a section contains radio options (asked of the whole section, so a search cannot
  // turn a radio group into plain rows)
  hasRadioOptions(section: CsSelectPopoverShownSection): boolean {
    return section.source.options.some(o => o.controlType === 'radio');
  }

  // get the currently selected radio value for a section, including a radio the search is hiding
  getRadioValue(section: CsSelectPopoverShownSection): string {
    const selected = section.source.options.find(o => o.selectState && o.controlType === 'radio');
    return selected?.value ?? '';
  }

  // handle radio selection change
  onRadioChange(section: CsSelectPopoverShownSection, value: string) {
    // update selectState for all radio options in the whole section, shown or hidden by the search
    section.source.options.forEach(o => {
      if (o.controlType === 'radio') {
        const wasSelected = o.selectState;
        o.selectState = o.value === value;
        // call handler if this option was just selected
        if (o.selectState && !wasSelected && o.handler) {
          void o.handler();
        }
      }
    });
  }

  // handle toggle change
  onToggleChange(option: CsSelectPopoverOption, checked: boolean) {
    option.selectState = checked;
    this.runMultiSelectHandler(option);
  }

  // handle hover on option (for desktop submenu trigger)
  onOptionHover(option: CsSelectPopoverOption) {
    if (this.bottomSheet) return;

    if (option.submenu) {
      // show submenu for items with submenu
      this.navigateToSubmenu(option);
    } else if (this.activeSubmenu) {
      // hide submenu when hovering over items without submenu
      this.hideSubmenu();
    }
  }

  // handle mouse leave on option (for desktop - hide submenu if leaving to non-submenu area)
  onOptionMouseLeave(_option: CsSelectPopoverOption) {
    // on desktop, we don't immediately hide submenu on option leave
    // because user might be moving to the submenu panel
    // submenu is hidden via onSubmenuMouseLeave instead
  }

  // handle mouse leave on submenu panel (for desktop - hide submenu)
  onSubmenuMouseLeave() {
    if (!this.bottomSheet && this.activeSubmenu) {
      this.hideSubmenu();
    }
  }
}
